import { Component, Host, State, Prop, h } from '@stencil/core';

declare global {
  interface Window {
    getTranslation: any;
  }
}

@Component({
  tag: 'kwo-modal',
  styleUrl: 'kwo-modal.scss',
  shadow: false,
})
export class KwoModal {
  @State() modalIsOpen: boolean;
  @State() isLoading: boolean = false;
  @Prop() emergency: boolean = false;
  @Prop() emergencyUrl: string = '';
  modalTriggers: NodeListOf<Element>;
  modalContent: HTMLElement;
  prevButton: { href: string, title: string } = { href: '', title: '' };
  nextButton: { href: string, title: string } = { href: '', title: '' };
  print: boolean = true;
  ajaxUrl: string = '';

  componentDidLoad() {
    this.modalIsOpen = false;
    this.modalTriggers = document.querySelectorAll('.openInModal, kwo-modal-map');
    this.modalContent = document.getElementById('modalContent');

    this.modalTriggers.forEach(link => {
      link.addEventListener('click', e => {

        e.preventDefault();
        this.ajaxUrl = link.getAttribute('href');
        if (e.target instanceof HTMLAnchorElement) {
          if (e.target.classList.contains('openInModal')) {
            this.ajaxUrl = e.target.href;
          } else {
            window.open(e.target.href, '_blank');
            return;
          }
        }
        if (this.ajaxUrl) {
          this.openModal();
        }
      });
    });

    if (this.emergency && this.emergencyUrl && !document.cookie.split(';').some((item) => item.trim().startsWith('kwo_hide_emergency_popup=hide'))) {
      this.ajaxUrl = this.emergencyUrl;
      this.openModal();
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  // load content of ajaxUrl into #modalContent
  async loadContent(url: string) {
    this.ajaxUrl = url;
    this.isLoading = true;
    this.modalContent && ( this.modalContent.innerHTML = '' );

    // get href and text for prev/next buttons
    const currentIndex = Array.from(this.modalTriggers).findIndex(link => link.getAttribute('href') === url);
    if (currentIndex >= 0) {
      const prevLink = this.modalTriggers[currentIndex - 1];
      const nextLink = this.modalTriggers[currentIndex + 1];

      this.prevButton = prevLink ? {
            href: prevLink.getAttribute('href'),
            title: prevLink.getAttribute('data-title')
      } : { href: '', title: '' };

      this.nextButton = nextLink ? {
            href: nextLink.getAttribute('href'),
            title: nextLink.getAttribute('data-title')
      } : { href: '', title: '' };
      this.print = this.modalTriggers[currentIndex].getAttribute('data-noprint') === '1' && false;
    }

    try {
      const response = await fetch(this.ajaxUrl + '?ajax=1');
      const data = await response.text();
      this.modalContent && (this.modalContent.innerHTML = data);
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  // open modal
  openModal(url: string = '') {
    url = url || this.ajaxUrl;
    this.loadContent(url);
    this.modalIsOpen = true;
    document.body.classList.add('overflow-hidden');
  }

  // close modal and remove content
  closeModal() {
    if (this.emergency && !document.cookie.split(';').some((item) => item.trim().startsWith('kwo_hide_emergency_popup=hide'))) {
      const expires = new Date();
      expires.setDate(expires.getDate() + 1);
      document.cookie = `kwo_hide_emergency_popup=hide; path=/; expires=${expires.toUTCString()}`;
    }
    this.ajaxUrl = '';
    this.modalContent && (this.modalContent.innerHTML = '');
    this.modalIsOpen = false;
    document.body.classList.remove('overflow-hidden');
  }

  printModal() {
    window.open(this.ajaxUrl + '?print=1', '_blank');
  }

  render() {
    return (
      <Host>
        <div class={`Modal__backdrop ${this.modalIsOpen ? 'open' : ''}`} onClick={() => this.closeModal()}></div>
        {(this.emergency && document.cookie.split(';').some((item) => item.trim().startsWith('kwo_hide_emergency_popup=hide'))) && (
          <button class="Modal__emergencyPopup Modal__button btn btn-primary d-flex justify-content-center align-items-center p-0" onClick={(() => this.openModal(this.emergencyUrl))}>
            <lg-icon name="info"></lg-icon>
          </button>
        )}
        <div class={`Modal container-xxl ${this.modalIsOpen ? 'open' : ''}`}>
          <div class="Modal__container">
            <div class="Modal__header d-flex flex-column gap-sm p-md">
              <button class="Modal__button btn btn-primary d-flex justify-content-center align-items-center p-0" onClick={() => this.closeModal()}>
                <lg-icon name="close"></lg-icon>
              </button>
              {(this.print && !this.emergency) && (
                <button class="Modal__button btn btn-primary d-none d-lg-flex justify-content-center align-items-center p-0" onClick={() => this.printModal()}>
                  <lg-icon name="print"></lg-icon>
                </button>
              )}
            </div>
            <div class={`Modal__loader justify-content-center align-items-center ${this.isLoading ? 'd-flex' : 'd-none'}`}>
              <lg-icon name="loader"></lg-icon>
            </div>
            <div class="d-flex flex-column h-100">
              <div class="Modal__content h-100 px-0 px-lg-lg" id="modalContent"></div>

              {(this.prevButton.href || this.nextButton.href) && (
                <div class="Modal__footer bg-lightyellow row p-lg">
                  <div class="col-12 col-lg-6">
                    {this.prevButton.href != '' && (
                      <button type="button" class="btn btn-tertiary text-nowrap text-truncate small" onClick={() => this.loadContent(this.prevButton.href)}>
                        <lg-icon name="arrow-up-left-thin" class="me-sm"></lg-icon>
                        {this.prevButton.title}
                      </button>
                    )}
                  </div>
                  <div class="col-12 col-lg-6 d-flex justify-content-lg-end">
                    {this.nextButton.href != '' && (
                      <button type="button" class="btn btn-tertiary text-nowrap text-truncate small" onClick={() => this.loadContent(this.nextButton.href)}>
                        {this.nextButton.title}
                        <lg-icon name="arrow-up-right-thin" class="ms-sm"></lg-icon>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
